/**
 * mention-suggestion — bridges the TipTap Mention extension's `suggestion`
 * lifecycle into the React-rendered `MentionSuggestionPopover`.
 *
 * `@tiptap/suggestion` calls our `render()` factory once per suggestion
 * session; we return four hooks (`onStart` / `onUpdate` / `onKeyDown` /
 * `onExit`) that mount, update, key-route, and unmount a React component.
 * This matches the pattern from the official TipTap mention example, but
 * using `ReactRenderer` (already a transitive dep via `@tiptap/react`)
 * instead of `tippy.js` (not a hoisted direct dep here — see
 * `MentionSuggestionPopover.tsx` for the full reasoning).
 *
 * The `getItems` function is supplied by the editor surface so the
 * member list can come from the TanStack Query board cache via
 * `useProjectMembersForMention(projectId)` without this module reaching
 * into React state directly.
 */

import { ReactRenderer } from '@tiptap/react'

import {
  type MentionSuggestionItem,
  MentionSuggestionPopover,
  type MentionSuggestionPopoverHandle,
} from './MentionSuggestionPopover'

interface SuggestionRenderProps {
  items: MentionSuggestionItem[]
  command: (attrs: { id: string; label: string }) => void
  clientRect?: (() => DOMRect | null) | null
  editor: unknown
}

interface SuggestionKeyDownProps {
  event: KeyboardEvent
}

export interface BuildMentionSuggestionOptions {
  /**
   * Returns the candidate list filtered by the current `query`. Should
   * be cheap — TipTap calls this on every keystroke after `@`.
   */
  getItems: (query: string) => MentionSuggestionItem[]
  /**
   * Phase 4d (chat composer) — override the trigger character. Defaults
   * to `'@'` so every existing call-site (card descriptions, comments)
   * stays unchanged. The chat composer additionally builds a `'#'`-
   * triggered config for channel mentions; see
   * `src/features/chat/lib/build-chat-mention-extensions.ts`.
   */
  char?: string
  /**
   * Fires `true` when the suggestion popover mounts and
   * `false` when it tears down. Lets a parent component (the chat
   * composer) gate "Enter sends the message" on whether the popover
   * has the keyboard. Default no-op.
   *
   * Necessary because ProseMirror's default Enter keymap (insert
   * paragraph) calls `event.preventDefault()` regardless, so the
   * `event.defaultPrevented` flag at the React wrapper level can't
   * distinguish "suggestion accepted Enter" from "ProseMirror inserted
   * a paragraph".
   */
  onPopoverStateChange?: (open: boolean) => void
}

/**
 * Build the `suggestion` config for the Mention extension.
 *
 * Returns the object that gets assigned to
 * `Mention.configure({ suggestion: ... })`. Calls `getItems` on every
 * query change; renders a portal-mounted React popover anchored to the
 * current trigger char.
 */
export function buildMentionSuggestion({
  getItems,
  char = '@',
  onPopoverStateChange,
}: BuildMentionSuggestionOptions) {
  return {
    char,

    items({ query }: { query: string }) {
      return getItems(query)
    },

    render() {
      let renderer: ReactRenderer<MentionSuggestionPopoverHandle> | null = null

      // Dismiss-from-popover. The popover invokes this on click-outside
      // (the editor's contenteditable already auto-exits when the caret
      // leaves the trigger range; this handles clicks on non-focusable
      // surfaces that don't blur the editor).
      const dismiss = () => {
        renderer?.destroy()
        renderer = null
        queueMicrotask(() => onPopoverStateChange?.(false))
      }

      return {
        onStart(props: SuggestionRenderProps) {
          renderer = new ReactRenderer(MentionSuggestionPopover, {
            props: {
              items: props.items,
              command: (item: MentionSuggestionItem) => {
                props.command({ id: item.id, label: item.name })
              },
              clientRect: props.clientRect ?? null,
              onDismiss: dismiss,
            },
            // We intentionally don't pass `editor` because
            // ReactRenderer's element is portaled to body inside the
            // component anyway — but TipTap's TS types want it.
            editor: props.editor as never,
          })
          onPopoverStateChange?.(true)
        },

        onUpdate(props: SuggestionRenderProps) {
          renderer?.updateProps({
            items: props.items,
            command: (item: MentionSuggestionItem) => {
              props.command({ id: item.id, label: item.name })
            },
            clientRect: props.clientRect ?? null,
            onDismiss: dismiss,
          })
        },

        onKeyDown(props: SuggestionKeyDownProps): boolean {
          if (props.event.key === 'Escape') {
            renderer?.destroy()
            renderer = null
            // Defer "popover closed" so a parent component's React-
            // synthetic-event handler (e.g. the chat composer's Enter →
            // submit gate) sees the popover as still-open during the
            // bubble that consumed this key.
            queueMicrotask(() => onPopoverStateChange?.(false))
            // Stop propagation so React's root-delegated keydown
            // handler doesn't see this Escape (avoids closing parent
            // modals on a mention-popover dismiss).
            props.event.stopPropagation()
            return true
          }
          // Forward arrow keys / Enter to the popover's imperative
          // handle so the highlighted item commits in-place.
          const handled = renderer?.ref?.onKeyDown(props.event) ?? false
          if (handled) {
            // Native keydown bubbles up the DOM to React's root-
            // container event listener. Stop it here so React's
            // delegated synthetic-event handlers never fire for keys
            // the popover consumed — fixes the "Enter picks the
            // mention AND sends the message" bug, since the chat
            // composer's `handleKeyDown` is gated by React's
            // delegation and `stopPropagation` is the only mechanism
            // that reliably suppresses it across React versions
            // regardless of microtask scheduling order.
            props.event.stopPropagation()
          }
          return handled
        },

        onExit() {
          renderer?.destroy()
          renderer = null
          // Same `queueMicrotask` rationale as the `Escape` branch above:
          // when the popover destroys itself synchronously after a commit
          // (the user pressed Enter to accept a mention), ProseMirror's
          // plugin chain runs before React's synthetic-event bubble
          // reaches the chat composer's handler. Without the deferral,
          // the popover-open ref is `false` by the time `handleKeyDown`
          // checks it, and the Enter that picked the mention also fires
          // `submit()`. Microtask runs at the end of the current task
          // (after React's bubble completes), so the ref stays `true`
          // for the duration of the gate check.
          queueMicrotask(() => onPopoverStateChange?.(false))
        },
      }
    },
  }
}
